import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Save01 } from 'untitledui-js-base';
import { AddNewAccountRequest } from '../../types/accident';

interface AddNewAccountDialogProps extends Omit<DialogProps, 'onSubmit'> {
  onSubmit: (newAccount: AddNewAccountRequest) => void;
  email?: string;
  phone?: string;
  isLoading?: boolean;
  userData?: { degree?: string; name?: string; surname?: string };
  operatorData?: { degree?: string; name?: string; surname?: string };
}
export const AddNewAccountDialog = ({
  open,
  onClose,
  onSubmit,
  email,
  phone,
  isLoading,
  userData,
  operatorData,
  ...props
}: AddNewAccountDialogProps) => {
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm<AddNewAccountRequest>({
    defaultValues: {
      degree: '',
      name: '',
      surname: '',
      email: email ?? '',
      phone: phone ?? ''
    }
  });

  const handleLoadFrom = (source: {
    degree?: string;
    name?: string;
    surname?: string;
  }) => {
    setValue('degree', source.degree ?? '');
    setValue('name', source.name ?? '');
    setValue('surname', source.surname ?? '');
  };

  useEffect(() => {
    reset({
      degree: '',
      name: '',
      surname: '',
      email: email ?? '',
      phone: phone ?? ''
    });
  }, [email, phone]);

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" {...props}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 16 }}>
        <DialogTitle id="alert-dialog-title">Založení účtu</DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          {(userData || operatorData) && (
            <Stack direction="row" spacing={1} mb={2}>
              {userData && (
                <Button variant="outlined" size="small" onClick={() => handleLoadFrom(userData)}>
                  Načíst od majitele
                </Button>
              )}
              {operatorData && (
                <Button variant="outlined" size="small" onClick={() => handleLoadFrom(operatorData)}>
                  Načíst od provozovatele
                </Button>
              )}
            </Stack>
          )}
          <Grid container spacing={2} paddingTop={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <Controller
                    control={control}
                    name="degree"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        label="Titul"
                        color="secondary"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={9}>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        label="Jméno"
                        color="secondary"
                        fullWidth
                        error={Boolean(errors.name)}
                        helperText={errors.name?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="surname"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Příjmení"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.surname)}
                    helperText={errors.surname?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type="email"
                    value={value}
                    onChange={onChange}
                    label="E-mail"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type="phone"
                    value={value}
                    onChange={onChange}
                    label="Telefon"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.phone)}
                    helperText={errors.phone?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            py: 4,
            justifyContent: 'center',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: 2
          }}
        >
          <Button
            type="button"
            onClick={() => {
              reset();
              onClose?.({}, 'escapeKeyDown');
            }}
          >
            Zrušit
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save01 />}
            size="large"
            color="success"
            loading={isLoading}
          >
            Vytvořit účet
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
